import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, ChevronRight, Phone } from "lucide-react";

const panelStyles = [
  {
    id: 1,
    name: "Shaker Panelling",
    description: "Classic grid pattern that works in hallways, living rooms, or bedrooms. Clean lines, timeless look.",
    image: "https://static.wixstatic.com/media/9778a6_200fa380af5841ff8908d471c7c401a1~mv2.jpg/v1/fill/w_400,h_400,al_c,q_80,usm_0.66_1.00_0.01/9778a6_200fa380af5841ff8908d471c7c401a1~mv2.jpg"
  },
  {
    id: 2,
    name: "Tongue & Groove",
    description: "Traditional vertical boards. Good for hallways, bathrooms (moisture-resistant MDF), utility rooms.",
    image: "https://static.wixstatic.com/media/9778a6_6b8f81e4c1964d6394475ed9f40eda12~mv2.jpg/v1/fill/w_400,h_400,al_c,q_80,usm_0.66_1.00_0.01/9778a6_6b8f81e4c1964d6394475ed9f40eda12~mv2.jpg"
  },
  {
    id: 3,
    name: "Slat Panelling",
    description: "Vertical slats that add texture. Often used behind TVs or as feature walls in bedrooms.",
    image: "https://static.wixstatic.com/media/9778a6_60106aae860142cc8967de54e90a133b~mv2.jpg/v1/fill/w_400,h_300,al_c,q_80,usm_0.66_1.00_0.01/9778a6_60106aae860142cc8967de54e90a133b~mv2.jpg"
  },
  {
    id: 4,
    name: "Half-Height Panelling",
    description: "Panelling fitted to dado height. Practical in hallways and stairs where walls take knocks.",
    image: "https://static.wixstatic.com/media/9778a6_8299edfc732243ddb7e786d66f3693e5~mv2.jpg/v1/fill/w_600,h_400,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/half%20wall%20paneling.jpg"
  }
];

const PanellingPage = () => {
  return (
    <div>
      <Helmet>
        <title>Wall Panelling | Shaker & Tongue and Groove | Expert Carpenter Greater Manchester</title>
        <meta name="description" content="Wall panelling fitted in Greater Manchester. Shaker, tongue & groove, slat panelling. Straight lines, level work, ready for painting. Call 07300 405045." />
        <link rel="canonical" href="https://expert-carpenter.co.uk/panelling" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does wall panelling cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "It depends on the style and area to cover. Simple shaker panelling on one wall is more affordable than full-height panelling throughout a room. Get in touch and I'll give you a quote."
                }
              },
              {
                "@type": "Question",
                "name": "How long does it take to fit panelling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "A single feature wall usually takes a day. A full room with panelling, filling and sanding is typically 2-3 days."
                }
              },
              {
                "@type": "Question",
                "name": "Do you paint the panelling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I prepare everything ready for painting - filled, caulked and sanded smooth. I can paint it too if you want, or you can do it yourself or get a decorator in."
                }
              },
              {
                "@type": "Question",
                "name": "Will panelling work on uneven walls?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Minor unevenness is usually fine. If walls are very uneven, I may need to pack out or skim areas first. I'll let you know when I see the job."
                }
              },
              {
                "@type": "Question",
                "name": "What material do you use?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Usually MDF for a smooth, paint-ready finish. For bathrooms I use moisture-resistant MDF. I can use other materials if you prefer."
                }
              },
              {
                "@type": "Question",
                "name": "Can I choose any colour?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes - once the panelling is fitted and prepped, you can paint it any colour you like. Most people go for white, grey or darker feature colours."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      {/* Hero */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <p className="text-[#C9A227] font-semibold tracking-wider uppercase mb-3 text-sm md:text-base">Additional Service</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                Wall Panelling
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Wall panelling fitted with straight lines, level work, and neat joints.
                Finished ready for painting in your choice of colour.
              </p>
              
              <ul className="space-y-4 mb-8 text-left max-w-md mx-auto lg:mx-0">
                {[
                  "Level and plumb installation",
                  "Neat joints throughout",
                  "Gaps filled and sanded",
                  "Ready for decorating"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-200 text-base md:text-lg">
                    <div className="w-7 h-7 rounded-full bg-[#C9A227]/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-[#C9A227]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              
              <div className="flex flex-col gap-4">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold text-lg h-14 px-8 w-full" data-testid="panelling-get-quote-btn">
                    Get a Free Quote
                    <ChevronRight className="ml-2 h-6 w-6" />
                  </Button>
                </Link>
                <a href="tel:07300405045" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold text-lg h-14 w-full">
                    <Phone className="mr-2 h-6 w-6" />
                    07300 405045
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10">
                <img
                  src="https://static.wixstatic.com/media/9778a6_c2273d780e2349d0926bd688b2cac0c2~mv2.jpg/v1/fill/w_600,h_700,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/02-white-shaker-wall-panel-living-room-design%20full%20wall.jpg"
                  alt="Wall panelling installation"
                  className="w-full h-[550px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Panel Styles */}
      <section className="py-14 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">Options</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#3A3A3A]">
              Panelling Styles
            </h2>
            <p className="mt-4 text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Different styles for different rooms. I can advise on what works best for your space.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {panelStyles.map((panel) => (
              <Card key={panel.id} className="group overflow-hidden border-0 shadow-lg bg-white">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={panel.image}
                    alt={panel.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold text-base text-[#3A3A3A] mb-1">{panel.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{panel.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-10 md:mt-16 text-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#2D5A4A] hover:bg-[#234839] h-14 px-10 text-lg font-bold w-full sm:w-auto">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 md:py-28 bg-[#f8f7f5]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-sm">The Process</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A3A3A] mb-4">
              How I Fit Panelling
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              { title: "Check the walls", desc: "I check walls are reasonably flat and deal with any issues before starting." },
              { title: "Set out the design", desc: "Mark up the layout so panels are evenly spaced and look right in the room." },
              { title: "Cut and fit", desc: "Panels cut accurately and fixed securely. I use adhesive and pins for a solid fix." },
              { title: "Fill and finish", desc: "All nail holes filled, joints caulked, edges sealed. Ready for painting." }
            ].map((step, i) => (
              <div key={i} className="flex gap-4 items-start bg-white p-5 rounded-xl shadow-sm">
                <div className="w-10 h-10 bg-[#2D5A4A] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-bold text-[#3A3A3A] mb-1">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-10 sm:py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <p className="text-[#2D5A4A] font-semibold tracking-wider uppercase mb-2 text-xs sm:text-sm">Common Questions</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#3A3A3A]">
              Wall Panelling FAQs
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              { 
                q: "How much does wall panelling cost?", 
                a: "It depends on the style and area to cover. Simple shaker panelling on one wall is more affordable than full-height panelling throughout a room. Get in touch and I'll give you a quote." 
              },
              { 
                q: "How long does it take to fit panelling?", 
                a: "A single feature wall usually takes a day. A full room with panelling, filling and sanding is typically 2-3 days." 
              },
              { 
                q: "Do you paint the panelling?", 
                a: "I prepare everything ready for painting - filled, caulked and sanded smooth. I can paint it too if you want, or you can do it yourself or get a decorator in." 
              },
              { 
                q: "Will panelling work on uneven walls?", 
                a: "Minor unevenness is usually fine. If walls are very uneven, I may need to pack out or skim areas first. I'll let you know when I see the job." 
              },
              { 
                q: "What material do you use?", 
                a: "Usually MDF for a smooth, paint-ready finish. For bathrooms I use moisture-resistant MDF. I can use other materials if you prefer." 
              },
              { 
                q: "Can I choose any colour?", 
                a: "Yes - once the panelling is fitted and prepped, you can paint it any colour you like. Most people go for white, grey or darker feature colours." 
              }
            ].map((faq, i) => (
              <div key={i} className="bg-[#f8f7f5] p-4 sm:p-5 rounded-xl">
                <h3 className="font-bold text-[#3A3A3A] mb-2 text-sm sm:text-base">{faq.q}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-28 bg-[#2D5A4A]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5">
            Interested in Panelling?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-8">
            Get in touch and I'll come out to discuss what you're after and give you a price.
          </p>
          <div className="flex flex-col gap-4">
            <Link to="/contact" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" className="bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold h-14 px-10 text-lg w-full">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <a href="tel:07300405045" className="w-full sm:w-auto sm:mx-auto">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold h-14 px-10 text-lg w-full">
                <Phone className="mr-2 h-6 w-6" />
                Call 07300 405045
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PanellingPage;
